import { type FC, useMemo, type RefObject } from 'react';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import SpreadsheetV16 from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import { registerHOTModulesV16 } from '@/support/hot-v16';
import { ScoringScaleEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/ScoringScaleEditorV16';
import {
  buildColumnsV16,
  buildCellsCallbackV16,
  buildAfterRendererCallbackV16,
} from './BulkEditObservationTypes.utils';

import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import type { ObservationTypeRow, ValidationData } from '@/utils/observationTypeValidation';

registerHOTModulesV16();
ScoringScaleEditorV16.register();

export interface BulkEditObservationTypesTableV16Props {
  data: Array<ObservationTypeRow>;
  innerRef: RefObject<HotTableRef | null>;
  observationTypes: Array<TeamGlossaryV1>;
  validationData: ValidationData | null;
  setInvalid: (invalid: boolean) => void;
}

const BulkEditObservationTypesTableV16: FC<BulkEditObservationTypesTableV16Props> = ({
  data,
  innerRef,
  observationTypes,
  validationData,
  setInvalid,
}) => {
  const columns = useMemo(
    () =>
      buildColumnsV16({
        validationData,
        observationTypes,
        hotTableRef: innerRef,
      }),
    [validationData, observationTypes, innerRef]
  );

  const settings = useMemo<SpreadsheetV16Settings>(
    () => ({
      columns,
      colHeaders: ['Observation type', 'Label', 'Scoring scale', 'Grading information'],
      rowHeaders: true,
      manualColumnResize: true,
      contextMenu: false as const,
      outsideClickDeselects: false,
      height: 600,
      rowHeights: 50,
      className: 'htMiddle',
      stretchH: 'all' as const,
    }),
    [columns]
  );

  const cellsCallback = useMemo(
    () => buildCellsCallbackV16(observationTypes, validationData),
    [observationTypes, validationData]
  );

  const afterRendererCallback = useMemo(() => buildAfterRendererCallbackV16(observationTypes), [observationTypes]);

  return (
    <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles" data-testid="observation-types-spreadsheet">
      <SpreadsheetV16
        data={data as unknown as Array<Record<string, unknown>>}
        settings={settings}
        innerRef={innerRef as RefObject<HotTableRef>}
        addCol={false}
        maxRows={data.length}
        setInvalid={setInvalid}
        modalView={true}
        fillHandle={false}
        cells={cellsCallback}
        customAfterRenderer={afterRendererCallback}
        testPrefix="bulk-edit-observation-types"
      />
    </div>
  );
};

export default BulkEditObservationTypesTableV16;
